import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Finish from '../components/Finish';
import Browser from './Browser';
import Preview from '../components/Browser/Preview';
import Loading from '../components/Browser/Loading';
// import ProjectSwitcher from '../components/Navigation/ProjectSwitcher';
import { reset, getConfig, addBuild, start, currencyChanged, refreshSelectedPrices } from '../actions';
import { getActiveCurrencyCode } from '../utils/currency';

/**
 * Root application component
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Matt Haynes <matthew.haynes@four.agency>
 * @package Minifigs
 */
const App = ({
    selected = {},
    projectTitle = '',
    finished = false,
    errorOnBuy = false,
    showNavigation = false,
    loaded = false,
    dispatch,
    sections = [],
    isCustomiseOverlayOpen = false,
    activeSection = null,
}) => {
    const [enableBuilder, setEnableBuilder] = useState(false);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [browserExpanded, setBrowserExpanded] = useState(false);

    useEffect(() => {
        const handler = () => {
            setBrowserExpanded((prev) => (prev ? prev : true));
        };
        window.addEventListener('builder:scrollExpandTrigger', handler);
        return () => window.removeEventListener('builder:scrollExpandTrigger', handler);
    }, []);

    // Poll for currency changes and dispatch to Redux so connected components re-render
    const currencyRef = useRef(getActiveCurrencyCode());
    useEffect(() => {
        const syncCurrency = () => {
            const next = getActiveCurrencyCode();
            if (next && next !== currencyRef.current) {
                currencyRef.current = next;
                dispatch(currencyChanged(next));
                dispatch(refreshSelectedPrices());
            }
        };

        syncCurrency();
        const intervalId = window.setInterval(syncCurrency, 500);
        window.addEventListener('focus', syncCurrency);
        document.addEventListener('visibilitychange', syncCurrency);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', syncCurrency);
            document.removeEventListener('visibilitychange', syncCurrency);
        };
    }, [dispatch]);

    const expandBrowser = useCallback(() => {
        if (!browserExpanded) setBrowserExpanded(true);
    }, [browserExpanded]);

    const expandPreview = useCallback(() => {
        if (browserExpanded) setBrowserExpanded(false);
    }, [browserExpanded]);

    const releaseToAuto = useCallback(() => {
        setBrowserExpanded(false);
    }, []);

    let classes = classNames('builder', {
        'builder-shownavigation': showNavigation,
        'builder-finished': finished,
        'builder-hasProjectTitle': enableBuilder,
        'builder-no-projectTitleExists': !enableBuilder,
        'builder-condensed': browserExpanded,
        'builder-product-overlay-active': isCustomiseOverlayOpen,
    });

    useEffect(() => {
        setBrowserExpanded(false);
        releaseToAuto();
    }, [activeSection, releaseToAuto]);

    useEffect(() => {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);

        const paramsProjectTitle = params.get('projectTitle');
        const paramsSkintone = params.get('skintone');
        if (!configLoaded) {
            dispatch(getConfig());
            setConfigLoaded(true);
        } else {
            if (paramsProjectTitle && paramsSkintone) {
                dispatch(addBuild({ projectTitle: paramsProjectTitle, skintone: paramsSkintone }));
                setEnableBuilder(true);
                url.search = '';
                window.history.replaceState({}, '', url);
            }
        }
    }, [dispatch, sections, configLoaded]);

    // On initial load, refresh stored prices to match current market context
    useEffect(() => {
        if (configLoaded) {
            dispatch(refreshSelectedPrices());
        }
    }, [configLoaded, dispatch]);

    // Collapse browser (show preview) whenever an items changes
    const selectionSignature = useMemo(() => {
        const core = ['hatsAndHair', 'heads', 'bodies', 'legs', 'display', 'keychain'];
        return core
            .map((k) => {
                const item = selected?.[k];
                if (!item) return `${k}:_`;
                const variantId = item.productMatchAttr?.id || item.attributes?.id || item.product?.id || '_';
                return `${k}:${variantId}`;
            })
            .join('|');
    }, [selected]);

    const prevSignatureRef = useRef();
    useEffect(() => {
        if (prevSignatureRef.current && prevSignatureRef.current !== selectionSignature) {
            expandPreview();
        }
        prevSignatureRef.current = selectionSignature;
    }, [selectionSignature, expandPreview]);

    const accessoriesLength = selected?.accessories?.length || 0;
    const prevAccessoriesLengthRef = useRef(accessoriesLength);
    useEffect(() => {
        if (prevAccessoriesLengthRef.current < accessoriesLength) {
            expandPreview();
        }
        prevAccessoriesLengthRef.current = accessoriesLength;
    }, [accessoriesLength, expandPreview]);

    if (!supportsFetch()) {
        return (
            <div>
                <div className="woocommerce-message">
                    Looks like your browser can&apos;t handle our Minifig Builder! Why not try using{' '}
                    <a href="https://getfirefox.com">Firefox</a> or{' '}
                    <a href="https://www.google.com/chrome/">Google Chrome</a>?<br />
                    All figure builder components are also available in the{' '}
                    <a href="https://minifigs.me/product-category/buy-bits/">&apos;Buy Bits&apos;</a> section.
                </div>

                <div style={{ height: '600px' }} />
            </div>
        );
    }

    const showFinish = finished && !errorOnBuy;

    return (
        <div className={classes}>
            <div className="builder-inner">
                <Preview
                    selected={selected}
                    setEnableBuilder={setEnableBuilder}
                    onPreviewSurfaceClick={expandPreview}
                />
                {loaded && (
                    <Browser
                        setEnableBuilder={setEnableBuilder}
                        enableBuilder={enableBuilder}
                        onBrowserSurfaceClick={expandBrowser}
                        releaseCondenseOverride={releaseToAuto}
                    />
                )}
            </div>
            {showFinish && (
                <div className="builder-finish-overlay">
                    <Finish
                        projectTitle={projectTitle}
                        resetAppState={() => {
                            dispatch(reset());
                            setEnableBuilder(false);

                            setTimeout(() => dispatch(start()), 0);
                        }}
                        onGoToCart={() => {
                            dispatch(reset());
                            setEnableBuilder(false);

                            setTimeout(() => {
                                window.location.href = '/cart';
                            }, 10);
                        }}
                    />
                </div>
            )}
            <Loading />
        </div>
    );
};

const supportsFetch = () => 'fetch' in window;

const mapStateToProps = (state) => ({
    selected: state.builds[state.currentBuildIndex].selected,
    projectTitle: state.builds[state.currentBuildIndex].projectTitle,
    finished: state.finished,
    errorOnBuy: state.errorOnBuy,
    showNavigation: state.showNavigation,
    loaded: state.loaded,
    sections: state.sections,
    // isScrolled no longer needed; event-driven expansion
    // activeFilter removed from condense logic
    isCustomiseOverlayOpen: state.isCustomiseOverlayOpen,
    activeSection: state.navigation.selected,
});

const productNodeShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
});

const selectedItemShape = PropTypes.shape({
    product: productNodeShape,
    category: PropTypes.object,
    attributes: PropTypes.any,
    addons: PropTypes.any,
    productMatchAttr: PropTypes.any,
    productFormJsonValues: PropTypes.object,
});

App.propTypes = {
    selected: PropTypes.shape({
        hatsAndHair: selectedItemShape,
        heads: selectedItemShape,
        bodies: selectedItemShape,
        legs: selectedItemShape,
        display: selectedItemShape,
        accessories: PropTypes.arrayOf(selectedItemShape),
    }),
    projectTitle: PropTypes.string,
    finished: PropTypes.bool,
    errorOnBuy: PropTypes.bool,
    showNavigation: PropTypes.bool,
    loaded: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    sections: PropTypes.arrayOf(PropTypes.any),
    // isScrolled removed
    // activeFilter prop removed
    isCustomiseOverlayOpen: PropTypes.bool,
    activeSection: PropTypes.string,
};

export default connect(mapStateToProps)(App);
